import * as stylex from '@stylexjs/stylex';
import { useEffect, useRef, useState } from 'react';
import { styles } from './styles';
import life01 from '../../../../assets/life/life-01.webp';
import life02 from '../../../../assets/life/life-02.webp';
import life03 from '../../../../assets/life/life-03.webp';
import life04 from '../../../../assets/life/life-04.webp';
import life05 from '../../../../assets/life/life-05.webp';
import life06 from '../../../../assets/life/life-06.webp';
import life07 from '../../../../assets/life/life-07.webp';
import life08 from '../../../../assets/life/life-08.webp';
import life09 from '../../../../assets/life/life-09.webp';
import life10 from '../../../../assets/life/life-10.webp';
import life11 from '../../../../assets/life/life-11.webp';
import life12 from '../../../../assets/life/life-12.webp';
import life13 from '../../../../assets/life/life-13.webp';
import life14 from '../../../../assets/life/life-14.webp';
import life15 from '../../../../assets/life/life-15.webp';
import life16 from '../../../../assets/life/life-16.webp';
import life17 from '../../../../assets/life/life-17.webp';
import life18 from '../../../../assets/life/life-18.webp';
import life19 from '../../../../assets/life/life-19.webp';
const TOP_ROW = [life01, life03, life05, life07, life09, life11, life13, life15, life17, life19];
const BOTTOM_ROW = [life02, life04, life06, life08, life10, life12, life14, life16, life18];

// Real tribys, real life: two counter-drifting rows of photo prints (tilted,
// white-bordered). Pointer devices get CSS marquees (hover pauses a row).
// Touch devices drift too, but through a native scroll container: a finger
// can grab a row any time; the drift resumes shortly after it lets go.
export function PhotoWall() {
  const touch = useTouchDevice();
  return <div {...stylex.props(styles.wall)}>
      <Row photos={TOP_ROW} touch={touch} />
      <Row photos={BOTTOM_ROW} touch={touch} reverse />
    </div>;
}
function useTouchDevice() {
  const [touch, setTouch] = useState(() => window.matchMedia('(pointer: coarse)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    const onChange = (e: MediaQueryListEvent) => setTouch(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return touch;
}
const DRIFT_PX_PER_S = 22;
const RESUME_DELAY_MS = 1500;

// Advance scrollLeft on a rAF loop; pause while the user is touching and for
// a beat afterwards. Both copies are identical, so wrapping scrollLeft by one
// copy-width whenever it leaves [0.25w, 1.25w) is invisible — the row is
// endless in both directions, dragged or drifting.
function useTouchDrift(touch: boolean, reverse: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!touch || !el) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    const speed = reverse ? -DRIFT_PX_PER_S : DRIFT_PX_PER_S;
    let raf = 0;
    let last = performance.now();
    let pos = -1; // uninitialized until images give the track real width
    let resumeAt = 0;
    const onTouchStart = () => {
      resumeAt = Infinity;
    };
    const onTouchEnd = () => {
      resumeAt = performance.now() + RESUME_DELAY_MS;
    };
    const step = (now: number) => {
      raf = requestAnimationFrame(step);
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      const copyWidth = el.scrollWidth / 2;
      if (copyWidth < el.clientWidth) return;
      if (pos < 0) {
        pos = copyWidth * 0.75;
        el.scrollLeft = pos;
        return;
      }
      const drifting = now >= resumeAt;
      if (drifting) {
        // A deviation means the user (or momentum) moved it — adopt that.
        if (Math.abs(el.scrollLeft - pos) > 2) pos = el.scrollLeft;
        pos += speed * dt;
      } else {
        pos = el.scrollLeft;
      }
      let wrapped = false;
      if (pos >= copyWidth * 1.25) {
        pos -= copyWidth;
        wrapped = true;
      }
      if (pos < copyWidth * 0.25) {
        pos += copyWidth;
        wrapped = true;
      }
      if (drifting || wrapped) el.scrollLeft = pos;
    };
    raf = requestAnimationFrame(step);
    el.addEventListener('touchstart', onTouchStart, {
      passive: true
    });
    el.addEventListener('touchend', onTouchEnd, {
      passive: true
    });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [touch, reverse]);
  return ref;
}
function Row({
  photos,
  touch,
  reverse = false
}: {
  photos: string[];
  touch: boolean;
  reverse?: boolean;
}) {
  const ref = useTouchDrift(touch, reverse);
  return <div ref={ref} {...stylex.props(styles.row, touch && styles.rowSwipe)}>
      <div {...stylex.props(styles.track, reverse && styles.trackReverse, touch && styles.trackStatic)}>
        {[false, true].map(clone => photos.map((src, i) => <img key={`${clone}-${src}`} {...stylex.props(styles.photo, i % 2 ? styles.tiltA : styles.tiltB)} src={src} alt={clone ? '' : 'A triby meeting in real life'} loading="lazy" aria-hidden={clone || undefined} />))}
      </div>
    </div>;
}